import { ALGORITHM_IDS } from '~/models/schemaVersion'
import type { WeekStartDay } from '~/models/settings'
import type { CalendarDate } from '~/types/dates'
import { startOfCalendarWeek } from './calendar'

/**
 * Estimated one-rep max, and the comparisons the weekly review reads (D-069).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE REP CAP MATTERS MORE THAN THE FORMULA, AND THAT WAS SAID BEFORE THE
 *  FORMULA WAS CHOSEN.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * D-066's strength rule compares a lift against **its own** trailing average,
 * and every standard rep-max formula is monotonic in load and reps — so a real
 * 5% decline reads as roughly 5% under any of them. What actually controls the
 * noise D-066 worried about is which sets may produce an estimate at all, since
 * estimates drawn from wildly different rep ranges are not comparable. Hence the
 * cap, which lives in `rules/thresholds.ts` as
 * `maxRepsCountedForEstimated1RM` and is injected here rather than hardcoded.
 *
 * A set above the cap is **excluded with a reason, never silently dropped**. The
 * set itself is still stored (see `models/strengthEntry.ts`): losing real
 * training data to a display rule would be the wrong trade.
 *
 * Pure: no clock, no storage, no formatting. The reference date and the
 * thresholds are injected.
 */

/** What one recorded set looks like to this module. */
export interface StrengthSetInput {
  readonly id: string
  readonly liftId: string
  readonly date: CalendarDate
  readonly loadKg: number
  readonly reps: number
  readonly bodyWeightKg?: number
}

/**
 * Epley: `load × (1 + reps / 30)`, published as `estimated_1rm_epley_v1`.
 *
 * **A single does not return the load.** Epley at one rep gives `load × 31/30`,
 * so a 60 kg single reads 62 kg. That is the formula D-069 approved, applied as
 * written rather than quietly special-cased — inventing a `reps === 1` branch
 * would be product policy this file has no authority to make. It does not affect
 * any rule, because the comparison is against the lift's own history and the
 * inflation is a constant factor. It does affect the number the user reads, and
 * that is recorded as **L-015**.
 */
export function estimateOneRepMax(loadKg: number, reps: number): number {
  return loadKg * (1 + reps / 30)
}

export interface ExcludedSet {
  readonly setId: string
  readonly reps: number
  readonly reason: 'repsAboveCap'
}

export type MissingReason = 'noSetsRecorded' | 'allSetsAboveRepCap'

/**
 * One lift's figure for one week.
 *
 * `missing` is a state, never a zero. A week with no qualifying set has no
 * estimate at all — reporting zero would read as a total loss of strength and
 * would drag any average that consumed it.
 */
export interface WeeklyLiftFigure {
  readonly weekStartDate: CalendarDate
  readonly status: 'present' | 'missing'
  readonly estimateKg?: number
  readonly bestSetId?: string
  readonly missingReason?: MissingReason
  /** Recorded so the interface can say why a set did not count. */
  readonly excludedSets: readonly ExcludedSet[]
  readonly algorithmId: string
}

export interface Estimate1RMThresholds {
  readonly maxRepsCountedForEstimated1RM: number
}

/**
 * The highest qualifying estimate among a week's sets (D-069).
 *
 * Highest rather than latest or averaged: the best set is the one that most
 * nearly tested the lift, and averaging a heavy single with a light back-off set
 * would report a strength the lifter never demonstrated.
 */
export function weeklyFigure(
  weekStartDate: CalendarDate,
  sets: readonly StrengthSetInput[],
  thresholds: Estimate1RMThresholds,
): WeeklyLiftFigure {
  const excludedSets: ExcludedSet[] = []
  let best: { readonly kg: number; readonly setId: string } | undefined

  for (const set of sets) {
    if (set.reps > thresholds.maxRepsCountedForEstimated1RM) {
      excludedSets.push({ setId: set.id, reps: set.reps, reason: 'repsAboveCap' })
      continue
    }
    const kg = estimateOneRepMax(set.loadKg, set.reps)
    if (best === undefined || kg > best.kg) best = { kg, setId: set.id }
  }

  if (best === undefined) {
    return {
      weekStartDate,
      status: 'missing',
      missingReason: sets.length === 0 ? 'noSetsRecorded' : 'allSetsAboveRepCap',
      excludedSets,
      algorithmId: ALGORITHM_IDS.estimated1RM,
    }
  }

  return {
    weekStartDate,
    status: 'present',
    estimateKg: best.kg,
    bestSetId: best.setId,
    excludedSets,
    algorithmId: ALGORITHM_IDS.estimated1RM,
  }
}

/** Group a lift's sets into calendar weeks, oldest first (D-034). */
export function weeklyFigures(
  sets: readonly StrengthSetInput[],
  weekStartDay: WeekStartDay,
  thresholds: Estimate1RMThresholds,
): readonly WeeklyLiftFigure[] {
  const byWeek = new Map<string, StrengthSetInput[]>()

  for (const set of sets) {
    const week = startOfCalendarWeek(set.date, weekStartDay)
    const existing = byWeek.get(week as string)
    if (existing === undefined) byWeek.set(week as string, [set])
    else existing.push(set)
  }

  return [...byWeek.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([week, weekSets]) => weeklyFigure(week as CalendarDate, weekSets, thresholds))
}

/** How many trailing weeks the four-week average is drawn from (D-066). */
const COMPARISON_WEEKS = 4

export interface LiftSummary {
  readonly liftId: string
  readonly current: WeeklyLiftFigure
  /** The week before the current one, if it produced a figure. */
  readonly previousWeekKg?: number
  /**
   * Mean of the weeks with a figure in the four weeks **before** the current
   * one. Weeks without a figure are omitted, never counted as zero — the same
   * rule the trailing weight average follows (D-036).
   */
  readonly fourWeekAverageKg?: number
  readonly weeksInFourWeekAverage: number
  /** The first figure this lift ever produced. */
  readonly startKg?: number
  /** Current estimate as a multiple of body weight, when body weight is known. */
  readonly relativeToBodyWeight?: number
  readonly changeVsPreviousWeekKg?: number
  readonly changeVsFourWeekPercent?: number
  readonly changeVsStartKg?: number
}

/**
 * Everything the review shows about one lift.
 *
 * The percentage against the four-week average is the figure D-066's decline
 * rule reads. **That rule is not implemented here**, and must not be: it also
 * needs a breadth condition across lifts and a two-consecutive-week condition,
 * and it belongs to the rules engine at Checkpoint 2.4. This module supplies the
 * number; nothing here decides what it means.
 */
export function summariseLift(
  liftId: string,
  sets: readonly StrengthSetInput[],
  reference: CalendarDate,
  weekStartDay: WeekStartDay,
  thresholds: Estimate1RMThresholds,
): LiftSummary {
  const currentWeek = startOfCalendarWeek(reference, weekStartDay)
  const figures = weeklyFigures(sets, weekStartDay, thresholds)

  const current =
    figures.find((figure) => figure.weekStartDate === currentWeek) ??
    weeklyFigure(currentWeek, [], thresholds)

  const earlier = figures.filter(
    (figure) => figure.weekStartDate < currentWeek && figure.status === 'present',
  )

  const previous = earlier.at(-1)
  const window = earlier.slice(-COMPARISON_WEEKS)
  const values = window.map((figure) => figure.estimateKg as number)

  const fourWeekAverageKg =
    values.length === 0 ? undefined : values.reduce((sum, value) => sum + value, 0) / values.length

  const startKg = earlier[0]?.estimateKg ?? current.estimateKg

  const bodyWeightKg = latestBodyWeight(sets, currentWeek)
  const currentKg = current.estimateKg

  return {
    liftId,
    current,
    ...(previous?.estimateKg === undefined ? {} : { previousWeekKg: previous.estimateKg }),
    ...(fourWeekAverageKg === undefined ? {} : { fourWeekAverageKg }),
    weeksInFourWeekAverage: values.length,
    ...(startKg === undefined ? {} : { startKg }),
    ...(currentKg === undefined || bodyWeightKg === undefined
      ? {}
      : { relativeToBodyWeight: currentKg / bodyWeightKg }),
    ...(currentKg === undefined || previous?.estimateKg === undefined
      ? {}
      : { changeVsPreviousWeekKg: currentKg - previous.estimateKg }),
    ...(currentKg === undefined || fourWeekAverageKg === undefined
      ? {}
      : {
          changeVsFourWeekPercent: ((currentKg - fourWeekAverageKg) / fourWeekAverageKg) * 100,
        }),
    ...(currentKg === undefined || startKg === undefined || earlier.length === 0
      ? {}
      : { changeVsStartKg: currentKg - startKg }),
  }
}

/** The most recent body weight recorded alongside a set, on or before the week. */
function latestBodyWeight(
  sets: readonly StrengthSetInput[],
  currentWeek: CalendarDate,
): number | undefined {
  const withWeight = sets
    .filter((set) => set.bodyWeightKg !== undefined && set.date >= currentWeek)
    .sort((left, right) => left.date.localeCompare(right.date))

  return withWeight.at(-1)?.bodyWeightKg
}
